import type {
  GroupLayer,
  ImageLayer,
  Layer,
  LayerType,
  QrLayer,
  ShapeLayer,
  TemplateDoc,
  TextLayer,
} from './template';

const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** Random base62 id. Uses Web Crypto (available in browsers and Node 20+). */
export function randomId(length = 16): string {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < length; i++) out += ALPHABET[bytes[i] % 62];
  return out;
}

export function newLayerId(): string {
  return `ly_${randomId(10)}`;
}

/** Generate a unique, API-friendly layer name such as `text_3`. */
export function uniqueLayerName(base: string, existing: Iterable<string>): string {
  const taken = new Set(existing);
  const clean =
    base
      .replace(/[.=&?#/\\]/g, '_')
      .trim()
      .slice(0, 56) || 'layer';
  if (!taken.has(clean)) return clean;
  for (let i = 2; i < 10000; i++) {
    const candidate = `${clean}_${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${clean}_${randomId(4)}`;
}

export const DEFAULT_FONT_FAMILY = 'Inter';

export function createTextLayer(partial: Partial<TextLayer> = {}): TextLayer {
  return {
    id: newLayerId(),
    name: 'text',
    type: 'text',
    x: 100,
    y: 100,
    width: 600,
    height: 120,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    text: 'Your text here',
    fontFamily: DEFAULT_FONT_FAMILY,
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: 64,
    color: '#111827',
    lineHeight: 1.2,
    letterSpacing: 0,
    align: 'left',
    verticalAlign: 'top',
    textTransform: 'none',
    textDecoration: 'none',
    autoFit: false,
    minFontSize: 12,
    maxLines: 0,
    padding: { top: 0, right: 0, bottom: 0, left: 0 },
    background: null,
    borderRadius: 0,
    strokeWidth: 0,
    strokeColor: '#000000',
    shadow: null,
    ...partial,
  };
}

export function createImageLayer(partial: Partial<ImageLayer> = {}): ImageLayer {
  return {
    id: newLayerId(),
    name: 'image',
    type: 'image',
    x: 100,
    y: 100,
    width: 400,
    height: 400,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    src: '',
    fit: 'cover',
    focusX: 0.5,
    focusY: 0.5,
    borderRadius: 0,
    borderWidth: 0,
    borderColor: '#000000',
    grayscale: false,
    blur: 0,
    brightness: 1,
    flipX: false,
    flipY: false,
    shadow: null,
    ...partial,
  };
}

export function createShapeLayer(partial: Partial<ShapeLayer> = {}): ShapeLayer {
  return {
    id: newLayerId(),
    name: 'shape',
    type: 'shape',
    x: 100,
    y: 100,
    width: 300,
    height: 300,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    shape: 'rectangle',
    fill: { type: 'solid', color: '#6366F1' },
    strokeWidth: 0,
    strokeColor: '#000000',
    strokeStyle: 'solid',
    borderRadius: 0,
    shadow: null,
    ...partial,
  };
}

export function createQrLayer(partial: Partial<QrLayer> = {}): QrLayer {
  return {
    id: newLayerId(),
    name: 'qr',
    type: 'qr',
    x: 100,
    y: 100,
    width: 240,
    height: 240,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    value: 'https://example.com',
    foreground: '#000000',
    background: '#FFFFFF',
    margin: 1,
    errorCorrection: 'M',
    dotStyle: 'square',
    eyeStyle: 'square',
    eyeColor: null,
    logo: '',
    logoSize: 0.22,
    ...partial,
  };
}

export function createGroupLayer(partial: Partial<GroupLayer> = {}): GroupLayer {
  return {
    id: newLayerId(),
    name: 'stack',
    type: 'group',
    x: 0,
    y: 0,
    width: 400,
    height: 400,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    direction: 'vertical',
    autoRowRatio: 1,
    gap: 0,
    padding: { top: 0, right: 0, bottom: 0, left: 0 },
    alignItems: 'start',
    justify: 'start',
    fill: null,
    borderRadius: 0,
    clip: false,
    ...partial,
  };
}

export function createLayer(type: LayerType, partial: Partial<Layer> = {}): Layer {
  switch (type) {
    case 'text':
      return createTextLayer(partial as Partial<TextLayer>);
    case 'image':
      return createImageLayer(partial as Partial<ImageLayer>);
    case 'shape':
      return createShapeLayer(partial as Partial<ShapeLayer>);
    case 'qr':
      return createQrLayer(partial as Partial<QrLayer>);
    case 'group':
      return createGroupLayer(partial as Partial<GroupLayer>);
  }
}

export function createEmptyDoc(width: number, height: number): TemplateDoc {
  return {
    width,
    height,
    background: { type: 'solid', color: '#FFFFFF' },
    layers: [],
  };
}

/** Deep clone a layer giving it a fresh id and a unique name. */
export function cloneLayer(layer: Layer, existingNames: Iterable<string>, offset = 20): Layer {
  const copy = structuredClone(layer) as Layer;
  copy.id = newLayerId();
  copy.name = uniqueLayerName(layer.name.replace(/_\d+$/, ''), existingNames);
  copy.x += offset;
  copy.y += offset;
  return copy;
}
